import Axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { toast } from 'sonner';
import { env } from 'config/env';
import { getObservedStytchOrganizationId } from 'lib/tenant-session';

declare module 'axios' {
    interface AxiosRequestConfig {
        /** The calling feature renders a localized error. Authentication still applies. */
        suppressErrorToast?: boolean;
    }
}

export const CLAIM_REVISION_CONFLICT_ERROR_CODE =
    'CLAIM_REVISION_CONFLICT' as const;

export type ApiClientErrorResponse = {
    error_code?: string;
    errors?: unknown;
    message?: string;
    requires_new_version?: boolean;
};

export const isClaimRevisionConflict = (error: unknown): boolean =>
    Axios.isAxiosError<ApiClientErrorResponse>(error) &&
    error.response?.status === 409 &&
    error.response.data.error_code === CLAIM_REVISION_CONFLICT_ERROR_CODE;

export const isForbiddenApiError = (error: unknown): boolean =>
    Axios.isAxiosError<ApiClientErrorResponse>(error) &&
    error.response?.status === 403;

function authRequestInterceptor(config: InternalAxiosRequestConfig) {
    if (config.headers) {
        config.headers.Accept = 'application/json';
        const observedOrganizationId = getObservedStytchOrganizationId();
        if (
            observedOrganizationId &&
            typeof config.url === 'string' &&
            config.url.startsWith('/app/')
        ) {
            config.headers['X-Atomix-Observed-Stytch-Organization'] =
                observedOrganizationId;
        }
    }

    config.withCredentials = true;
    return config;
}

export const api = Axios.create({
    baseURL: env.API_URL,
});

export const handleApiResponseError = (
    error: AxiosError<ApiClientErrorResponse>,
): Promise<never> => {
    if (Axios.isCancel(error)) return Promise.reject(error);
    if (error.response?.status === 401) {
        const currentPath = window.location.pathname;
        const searchParams = new URLSearchParams();
        if (currentPath !== '/authenticate') {
            searchParams.set('redirectTo', currentPath);
            window.location.href = `/authenticate?${searchParams.toString()}`;
        }
        return Promise.reject(error);
    }

    // Claim workbenches render revision conflicts inline with the rebased
    // draft. Only the typed conflict response bypasses the global error toast.
    if (isClaimRevisionConflict(error)) {
        return Promise.reject(error);
    }

    if (error.config?.suppressErrorToast) return Promise.reject(error);

    if (error.response?.status === 400 && error.response?.data.errors) {
        toast.error(
            `Validation errors occurred: ${JSON.stringify(error.response?.data.errors)}`,
        );
    } else if (error.response?.data.message) {
        toast.error(error.response?.data.message);
    } else if (!error.response?.data.requires_new_version) {
        toast.error(
            'An unknown error occurred. Please contact support if the issue persists.',
        );
    }

    return Promise.reject(error);
};

api.interceptors.request.use(authRequestInterceptor);
api.interceptors.response.use((response) => {
    return response.data;
}, handleApiResponseError);

export type ErrorMessageResponse = {
    message: string;
};

export type RequiresNewVersionResponse = {
    requires_new_version: boolean;
};
